import React, { useEffect, useMemo, useRef, useState } from "react";

import { Tab, Button, Input, Pagination } from "semantic-ui-react";

import moment from "moment";

import DateTextBox from "../common/DateTextBox";
import PaymentSlipTable, { COLOR } from "./PaymentSlipTable";

import "./CardPaymentSlip.scss";

import { parseBeOrAdDate } from "react-lib/utils/dateUtils";

type CardPaymentSlipProps = {
  buttonLoadCheck?: any;
  onEvent?: any;
  appointmentPaymentRelationList?: any;
  userDetail?: any;
  appointmentPaymentAction?: any;
  isPaymentRelation?: boolean;
};

const ROWS_PER_PAGE = 15;

const CardPaymentSlip: React.FC<CardPaymentSlipProps> = (props) => {
  // === useState ===
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [filterName, setFilterName] = useState("");
  const [filteredPayment, setFilteredPayment] = useState<any[]>([]);
  const [activePage, setActivePage] = useState(1);

  const [selectedPatientId, setSelectedPatientId] = useState<string | undefined>(undefined);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [filterDueDateStart, setFilterDueDateStart] = useState<string | undefined>(undefined);
  const [filterDueDateEnd, setFilterDueDateEnd] = useState<string | undefined>(undefined);
  const [filterPatientPaidStart, setFilterPatientPaidStart] = useState<string | undefined>(
    undefined,
  );
  const [filterPatientPaidEnd, setFilterPatientPaidEnd] = useState<string | undefined>(undefined);
  const [filterCheckPaymentStart, setFilterCheckPaymentStart] = useState<string | undefined>(
    undefined,
  );
  const [filterCheckPaymentEnd, setFilterCheckPaymentEnd] = useState<string | undefined>(undefined);
  const [filterRequestRefundStart, setFilterRequestRefundStart] = useState<string | undefined>(
    undefined,
  );
  const [filterRequestRefundEnd, setFilterRequestRefundEnd] = useState<string | undefined>(
    undefined,
  );
  const [selectedAppointmentStatuses, setSelectedAppointmentStatuses] = useState<string[]>([]);
  const [selectedDoctorId, setSelectedDoctorId] = useState<string | undefined>(undefined);
  const [filterAppointmentStart, setFilterAppointmentStart] = useState<string | undefined>(
    undefined,
  );
  const [filterAppointmentEnd, setFilterAppointmentEnd] = useState<string | undefined>(undefined);
  const [masterPaymentList, setMasterPaymentList] = useState<any[]>([]);

  // === useRef ===
  const tableRef = useRef<any>(null);

  // === utility ===
  const getPaginatedData = (
    list: any[],
    statuses: (string | number)[] | string | number,
    excludePatientStatus5: boolean = false,
    excludeRefunded: boolean = false,
  ) => {
    const statusArray = Array.isArray(statuses) ? statuses : [statuses];

    const filtered = list.filter((p: any) => {
      const appointmentStatus = p.patient_appointment?.status;

      if (excludePatientStatus5 && appointmentStatus === 5) return false;

      if (excludeRefunded && p.status === "REFUNDED") return false;

      const statusMatch = statusArray.includes(p.status);
      const patientAppointmentMatch = appointmentStatus
        ? statusArray.includes(appointmentStatus)
        : false;

      return statusMatch || patientAppointmentMatch;
    });

    const data = filtered.slice((activePage - 1) * ROWS_PER_PAGE, activePage * ROWS_PER_PAGE);
    const emptyRowsCount = ROWS_PER_PAGE - data.length;
    const emptyRows = Array.from({ length: Math.max(emptyRowsCount, 0) });
    const totalPages = Math.max(1, Math.ceil(filtered.length / ROWS_PER_PAGE));

    return { filtered, data, emptyRows, totalPages };
  };

  // === useMemo ===
  const filterPayment = useMemo(() => {
    let list = [...masterPaymentList];

    if (filterName?.trim()) {
      const lowerName = filterName.toLowerCase();
      list = list.filter((p: any) => {
        const firstName = p.patient_appointment?.patient_first_name?.toLowerCase() || "";
        const lastName = p.patient_appointment?.patient_last_name?.toLowerCase() || "";
        const fullName = `${firstName} ${lastName}`.trim();

        return (
          firstName.includes(lowerName) ||
          lastName.includes(lowerName) ||
          fullName.includes(lowerName)
        );
      });
    }

    return list;
  }, [masterPaymentList, filterName]);

  // === derived ===
  const {
    data: requestedData,
    emptyRows: requestedEmptyRows,
    totalPages: totalRequestedPages,
  } = getPaginatedData(filteredPayment, "REQUESTED", true);

  const {
    data: requestedConfirmData,
    emptyRows: requestedConfirmEmptyRows,
    totalPages: totalRequestedConfirmPages,
  } = getPaginatedData(filteredPayment, "REQUESTED_CONFIRM", true);

  const {
    data: confirmedData,
    emptyRows: confirmedEmptyRows,
    totalPages: totalConfirmedPages,
  } = getPaginatedData(filteredPayment || [], "CONFIRMED", true);

  const {
    data: rejectedData,
    emptyRows: rejectedEmptyRows,
    totalPages: totalRejectedPages,
  } = getPaginatedData(filteredPayment || [], "REJECTED", true);

  const {
    data: requestedRefundData,
    emptyRows: requestedRefundEmptyRows,
    totalPages: totalRequestedRefundPages,
  } = getPaginatedData(filteredPayment || [], ["REQUESTED_REFUND", "REFUNDING", 5], false, true);

  const {
    data: refundedData,
    emptyRows: refundedEmptyRows,
    totalPages: totalRefundedPages,
  } = getPaginatedData(filteredPayment || [], "REFUNDED");

  // === useEffect ===
  useEffect(() => {
    props.onEvent({
      message: "HandleGetAppointmentPaymentRelationList",
      params: { isPaymentRelation: props.isPaymentRelation },
    });
  }, [props.isPaymentRelation]);

  useEffect(() => {
    const initialData = (props.appointmentPaymentRelationList || []).filter(
      (p: any) => p.status === "REQUESTED",
    );
    setFilteredPayment(initialData);
  }, [props.appointmentPaymentRelationList]);

  useEffect(() => {
    if (props.appointmentPaymentRelationList?.length) {
      setMasterPaymentList((prev) => {
        if (prev.length === 0) return props.appointmentPaymentRelationList;
        return prev;
      });

      setFilteredPayment(props.appointmentPaymentRelationList);
    }
  }, [props.appointmentPaymentRelationList]);

  useEffect(() => {
    setFilteredPayment(filterPayment);
  }, [filterPayment]);

  useEffect(() => {
    if (props.appointmentPaymentRelationList?.length) {
      setMasterPaymentList([...props.appointmentPaymentRelationList]);
    } else {
      setMasterPaymentList([]);
    }
  }, [props.appointmentPaymentRelationList]);

  // === handlers ===
  const handleTabChange = (e: any, { activeIndex }: any) => {
    setFilterName("");
    setFilterDueDateStart(undefined);
    setFilterDueDateEnd(undefined);
    setFilterPatientPaidStart(undefined);
    setFilterPatientPaidEnd(undefined);
    setFilterCheckPaymentStart(undefined);
    setFilterCheckPaymentEnd(undefined);
    setFilterRequestRefundStart(undefined);
    setFilterRequestRefundEnd(undefined);
    setSelectedAppointmentStatuses([]);
    setSelectedDoctorId(undefined);
    setFilterAppointmentStart(undefined);
    setFilterAppointmentEnd(undefined);
    setActivePage(1);

    if (tableRef.current?.resetColumnWidths) {
      tableRef.current.resetColumnWidths();
    }

    props.onEvent({
      message: "HandleGetAppointmentPaymentRelationList",
      params: { isPaymentRelation: props.isPaymentRelation },
    });
    setActiveTabIndex(activeIndex);
  };

  const handlePageChange = (_e: any, { activePage }: any) => {
    setActivePage(activePage);
  };

  const handleSearchPaymentClick = () => {
    const formatDate = (dateStr?: string, isEnd = false) => {
      if (!dateStr) return undefined;
      const date = new Date(dateStr);
      if (isEnd) date.setHours(23, 59, 59, 999);
      return date.toISOString().split("T")[0];
    };

    props.onEvent({
      message: "HandleFilterAppointmentPaymentRelationList",
      params: {
        patient: selectedPatientId,
        statuses: selectedStatuses.length ? selectedStatuses : undefined,
        due_date_start: formatDate(filterDueDateStart),
        due_date_end: formatDate(filterDueDateEnd, true),
        patient_paid_date_start: formatDate(filterPatientPaidStart),
        patient_paid_date_end: formatDate(filterPatientPaidEnd, true),
        check_payment_date_start: formatDate(filterCheckPaymentStart),
        check_payment_date_end: formatDate(filterCheckPaymentEnd, true),
        request_refund_date_start: formatDate(filterRequestRefundStart),
        request_refund_date_end: formatDate(filterRequestRefundEnd, true),
        patient_appointment_statuses: selectedAppointmentStatuses.length
          ? selectedAppointmentStatuses
          : undefined,
        doctor: selectedDoctorId,
        patient_appointment_start_date: formatDate(filterAppointmentStart),
        patient_appointment_end_date: formatDate(filterAppointmentEnd, true),
        isPaymentRelation: props.isPaymentRelation,
      },
    });

    setActivePage(1);
  };

  // === internal components ===
  const FilterDate = ({
    value,
    onChange,
    placeholder = "dd/mm/yyyy",
    inputStyle,
  }: {
    value: string;
    onChange: (val: string) => void;
    placeholder?: string;
    inputStyle?: React.CSSProperties;
  }) => {
    const [localValue, setLocalValue] = React.useState(
      value ? moment(value).add(543, "years").format("DD/MM/YYYY") : "",
    );

    useEffect(() => {
      setLocalValue(value ? moment(value).add(543, "years").format("DD/MM/YYYY") : "");
    }, [value]);

    return (
      <DateTextBox
        placeholder={placeholder}
        value={localValue}
        inputStyle={inputStyle}
        onChange={(val) => {
          setLocalValue(val);

          if (!val) {
            onChange("");
            return;
          }

          const isFullDate = /^\d{2}\/\d{2}\/\d{4}$/.test(val);
          if (!isFullDate) return;

          const date = parseBeOrAdDate(val);
          if (date && date.isValid()) {
            onChange(date.format("YYYY-MM-DD"));
          }
        }}
      />
    );
  };

  return (
    <div className="card-slip-membership" style={{ padding: "15px 0" }}>
      <Tab
        activeIndex={activeTabIndex}
        menu={{ pointing: true, secondary: true }}
        panes={[
          {
            menuItem: {
              key: "requested",
              content: (
                <div>
                  <span>รอชำระ</span>
                  <div className="badge">
                    {
                      masterPaymentList.filter(
                        (p: any) => p.status === "REQUESTED" && p.patient_appointment?.status !== 5,
                      ).length
                    }
                  </div>
                </div>
              ),
            },
            render: () => (
              <Tab.Pane>
                <div
                  style={{ height: "calc(100vh - 250px)", overflowY: "auto", paddingRight: "8px" }}
                >
                  <div
                    style={{
                      marginBottom: "1rem",
                      display: "flex",
                      alignItems: "flex-end",
                      gap: "1rem",
                    }}
                  >
                    <div style={{ display: "flex", flexDirection: "column", width: "200px" }}>
                      <span style={{ marginBottom: "0.5rem", whiteSpace: "nowrap" }}>ชื่อ-นามสกุล ผู้รับบริการ</span>
                      <Input
                        icon={{ name: "search", circular: false, link: false }}
                        placeholder="ค้นหาด้วยชื่อ หรือ นามสกุล"
                        value={filterName}
                        onChange={(e) => setFilterName(e.target.value)}
                        style={{ width: "100%" }}
                      />
                    </div>

                    <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                      <span>วันที่ครบกำหนดชำระ</span>
                      <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
                        <FilterDate
                          value={filterDueDateStart || ""}
                          onChange={setFilterDueDateStart}
                        />
                        <FilterDate value={filterDueDateEnd || ""} onChange={setFilterDueDateEnd} />
                      </div>
                    </div>

                    <div style={{ display: "flex", gap: "1rem", marginTop: "1.5rem", marginLeft: "auto" }}>
                      <Button
                        style={{ color: "white", backgroundColor: COLOR.primary }}
                        loading={props.buttonLoadCheck?.filterPaymentList === "LOADING"}
                        onClick={handleSearchPaymentClick}
                      >
                        ค้นหา
                      </Button>
                      <Button
                        onClick={() => {
                          setFilterName("");
                          setFilterDueDateStart(undefined);
                          setFilterDueDateEnd(undefined);
                        }}
                      >
                        ล้างตัวกรอง
                      </Button>
                    </div>
                  </div>

                  <div style={{ fontWeight: "normal" }}>
                    <PaymentSlipTable
                      ref={tableRef}
                      data={requestedData}
                      emptyRows={requestedEmptyRows}
                      status="REQUESTED"
                      onEvent={props.onEvent}
                      appointmentPaymentRelationList={props.appointmentPaymentRelationList}
                      userDetail={props.userDetail}
                      isPaymentRelation={props.isPaymentRelation}
                    />
                  </div>
                  <div style={{ display: "flex", justifyContent: "center", marginTop: "1rem" }}>
                    <Pagination
                      activePage={activePage}
                      totalPages={totalRequestedPages}
                      onPageChange={handlePageChange}
                    />
                  </div>
                </div>
              </Tab.Pane>
            ),
          },
          {
            menuItem: {
              key: "requested confirm",
              content: (
                <div>
                  <span>แจ้งชำระ</span>
                  <div className="badge">
                    {
                      masterPaymentList.filter(
                        (p: any) =>
                          p.status === "REQUESTED_CONFIRM" && p.patient_appointment?.status !== 5,
                      ).length
                    }
                  </div>
                </div>
              ),
            },
            render: () => (
              <Tab.Pane>
                <div
                  style={{ height: "calc(100vh - 250px)", overflowY: "auto", paddingRight: "8px" }}
                >
                  <div
                    style={{
                      marginBottom: "1rem",
                      display: "flex",
                      alignItems: "flex-end",
                      gap: "1rem",
                    }}
                  >
                    <div style={{ display: "flex", flexDirection: "column", width: "200px" }}>
                      <span style={{ marginBottom: "0.5rem", whiteSpace: "nowrap" }}>ชื่อ-นามสกุล ผู้รับบริการ</span>
                      <Input
                        icon={{ name: "search", circular: false, link: false }}
                        placeholder="ค้นหาด้วยชื่อ หรือ นามสกุล"
                        value={filterName}
                        onChange={(e) => setFilterName(e.target.value)}
                        style={{ width: "100%" }}
                      />
                    </div>

                    <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                      <span>วันที่ผู้รับบริการโอน</span>
                      <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
                        <FilterDate
                          value={filterPatientPaidStart || ""}
                          onChange={setFilterPatientPaidStart}
                        />
                        <FilterDate
                          value={filterPatientPaidEnd || ""}
                          onChange={setFilterPatientPaidEnd}
                        />
                      </div>
                    </div>

                    <div style={{ display: "flex", gap: "1rem", marginTop: "1.5rem", marginLeft: "auto" }}>
                      <Button
                        style={{ color: "white", backgroundColor: COLOR.primary }}
                        loading={props.buttonLoadCheck?.filterPaymentList === "LOADING"}
                        onClick={handleSearchPaymentClick}
                      >
                        ค้นหา
                      </Button>
                      <Button
                        onClick={() => {
                          setFilterName("");
                          setFilterPatientPaidStart(undefined);
                          setFilterPatientPaidEnd(undefined);
                        }}
                      >
                        ล้างตัวกรอง
                      </Button>
                    </div>
                  </div>

                  <div style={{ fontWeight: "normal" }}>
                    <PaymentSlipTable
                      ref={tableRef}
                      data={requestedConfirmData}
                      emptyRows={requestedConfirmEmptyRows}
                      status="REQUESTED_CONFIRM"
                      buttonLoadCheck={props.buttonLoadCheck}
                      onEvent={props.onEvent}
                      appointmentPaymentRelationList={props.appointmentPaymentRelationList}
                      userDetail={props.userDetail}
                      isPaymentRelation={props.isPaymentRelation}
                    />
                  </div>
                  <div style={{ display: "flex", justifyContent: "center", marginTop: "1rem" }}>
                    <Pagination
                      activePage={activePage}
                      totalPages={totalRequestedConfirmPages}
                      onPageChange={handlePageChange}
                    />
                  </div>
                </div>
              </Tab.Pane>
            ),
          },
          {
            menuItem: {
              key: "confirmed",
              content: (
                <div>
                  <span>ชำระแล้ว</span>
                  <div className="badge">
                    {
                      masterPaymentList.filter(
                        (p: any) => p.status === "CONFIRMED" && p.patient_appointment?.status !== 5,
                      ).length
                    }
                  </div>{" "}
                </div>
              ),
            },
            render: () => (
              <Tab.Pane>
                <div
                  style={{ height: "calc(100vh - 250px)", overflowY: "auto", paddingRight: "8px" }}
                >
                  <div
                    style={{
                      marginBottom: "1rem",
                      display: "flex",
                      alignItems: "flex-end",
                      gap: "1rem",
                    }}
                  >
                    <div style={{ display: "flex", flexDirection: "column", width: "200px" }}>
                      <span style={{ marginBottom: "0.5rem", whiteSpace: "nowrap" }}>ชื่อ-นามสกุล ผู้รับบริการ</span>
                      <Input
                        icon={{ name: "search", circular: false, link: false }}
                        placeholder="ค้นหาด้วยชื่อ หรือ นามสกุล"
                        value={filterName}
                        onChange={(e) => setFilterName(e.target.value)}
                        style={{ width: "100%" }}
                      />
                    </div>

                    <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                      <span>วันที่ผู้รับบริการโอน</span>
                      <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                        <FilterDate
                          value={filterPatientPaidStart || ""}
                          onChange={setFilterPatientPaidStart}
                          inputStyle={{ width: "165px" }}
                        />
                        <FilterDate
                          value={filterPatientPaidEnd || ""}
                          onChange={setFilterPatientPaidEnd}
                          inputStyle={{ width: "165px" }}
                        />
                      </div>
                    </div>

                    <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                      <span>วันที่ตรวจสอบหลักฐาน</span>
                      <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                        <FilterDate
                          value={filterCheckPaymentStart || ""}
                          onChange={setFilterCheckPaymentStart}
                          inputStyle={{ width: "165px" }}
                        />
                        <FilterDate
                          value={filterCheckPaymentEnd || ""}
                          onChange={setFilterCheckPaymentEnd}
                          inputStyle={{ width: "165px" }}
                        />
                      </div>
                    </div>

                    <div style={{ display: "flex", gap: "1rem", marginTop: "1.5rem", marginLeft: "auto" }}>
                      <Button
                        style={{ color: "white", backgroundColor: COLOR.primary }}
                        loading={props.buttonLoadCheck?.filterPaymentList === "LOADING"}
                        onClick={handleSearchPaymentClick}
                      >
                        ค้นหา
                      </Button>
                      <Button
                        onClick={() => {
                          setFilterName("");
                          setFilterPatientPaidStart(undefined);
                          setFilterPatientPaidEnd(undefined);
                          setFilterCheckPaymentStart(undefined);
                          setFilterCheckPaymentEnd(undefined);
                        }}
                      >
                        ล้างตัวกรอง
                      </Button>
                    </div>
                  </div>
                  <div style={{ fontWeight: "normal" }}>
                    <PaymentSlipTable
                      ref={tableRef}
                      data={confirmedData}
                      emptyRows={confirmedEmptyRows}
                      status="CONFIRMED"
                      onEvent={props.onEvent}
                      appointmentPaymentRelationList={props.appointmentPaymentRelationList}
                      userDetail={props.userDetail}
                      isPaymentRelation={props.isPaymentRelation}
                    />
                  </div>
                  <div style={{ display: "flex", justifyContent: "center", marginTop: "1rem" }}>
                    <Pagination
                      activePage={activePage}
                      totalPages={totalConfirmedPages}
                      onPageChange={handlePageChange}
                    />
                  </div>
                </div>
              </Tab.Pane>
            ),
          },
          {
            menuItem: {
              key: "rejected",
              content: (
                <div>
                  <span>หลักฐานมีปัญหา</span>
                  <div className="badge">
                    {
                      masterPaymentList.filter(
                        (p: any) => p.status === "REJECTED" && p.patient_appointment?.status !== 5,
                      ).length
                    }
                  </div>{" "}
                </div>
              ),
            },
            render: () => (
              <Tab.Pane>
                <div
                  style={{ height: "calc(100vh - 250px)", overflowY: "auto", paddingRight: "8px" }}
                >
                  <div
                    style={{
                      marginBottom: "1rem",
                      display: "flex",
                      alignItems: "flex-end",
                      gap: "1rem",
                    }}
                  >
                    <div style={{ display: "flex", flexDirection: "column", width: "200px" }}>
                      <span style={{ marginBottom: "0.5rem", whiteSpace: "nowrap" }}>ชื่อ-นามสกุล ผู้รับบริการ</span>
                      <Input
                        icon={{ name: "search", circular: false, link: false }}
                        placeholder="ค้นหาด้วยชื่อ หรือ นามสกุล"
                        value={filterName}
                        onChange={(e) => setFilterName(e.target.value)}
                        style={{ width: "100%" }}
                      />
                    </div>

                    <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                      <span>วันที่ผู้รับบริการโอน</span>
                      <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                        <FilterDate
                          value={filterPatientPaidStart || ""}
                          onChange={setFilterPatientPaidStart}
                          inputStyle={{ width: "165px" }}
                        />
                        <FilterDate
                          value={filterPatientPaidEnd || ""}
                          onChange={setFilterPatientPaidEnd}
                          inputStyle={{ width: "165px" }}
                        />
                      </div>
                    </div>

                    <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                      <span>วันที่ตรวจสอบหลักฐาน</span>
                      <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                        <FilterDate
                          value={filterCheckPaymentStart || ""}
                          onChange={setFilterCheckPaymentStart}
                          inputStyle={{ width: "165px" }}
                        />
                        <FilterDate
                          value={filterCheckPaymentEnd || ""}
                          onChange={setFilterCheckPaymentEnd}
                          inputStyle={{ width: "165px" }}
                        />
                      </div>
                    </div>

                    <div style={{ display: "flex", gap: "1rem", marginTop: "1.5rem", marginLeft: "auto" }}>
                      <Button
                        style={{ color: "white", backgroundColor: COLOR.primary }}
                        loading={props.buttonLoadCheck?.filterPaymentList === "LOADING"}
                        onClick={handleSearchPaymentClick}
                      >
                        ค้นหา
                      </Button>
                      <Button
                        onClick={() => {
                          setFilterName("");
                          setFilterPatientPaidStart(undefined);
                          setFilterPatientPaidEnd(undefined);
                          setFilterCheckPaymentStart(undefined);
                          setFilterCheckPaymentEnd(undefined);
                        }}
                      >
                        ล้างตัวกรอง
                      </Button>
                    </div>
                  </div>
                  <div style={{ fontWeight: "normal" }}>
                    <PaymentSlipTable
                      ref={tableRef}
                      data={rejectedData}
                      emptyRows={rejectedEmptyRows}
                      status="REJECTED"
                      onEvent={props.onEvent}
                      appointmentPaymentRelationList={props.appointmentPaymentRelationList}
                      userDetail={props.userDetail}
                      isPaymentRelation={props.isPaymentRelation}
                    />
                  </div>
                  <div style={{ display: "flex", justifyContent: "center", marginTop: "1rem" }}>
                    <Pagination
                      activePage={activePage}
                      totalPages={totalRejectedPages}
                      onPageChange={handlePageChange}
                    />
                  </div>
                </div>
              </Tab.Pane>
            ),
          },
          {
            menuItem: {
              key: "requested refund",
              content: (
                <div>
                  <span>รอคืนเงิน</span>
                  <div className="badge">
                    {
                      masterPaymentList.filter(
                        (p: any) =>
                          (p.status === "REQUESTED_REFUND" ||
                            p.status === "REFUNDING" ||
                            p.patient_appointment?.status === 5) &&
                          p.status !== "REFUNDED",
                      ).length
                    }
                  </div>
                </div>
              ),
            },
            render: () => (
              <Tab.Pane>
                <div
                  style={{ height: "calc(100vh - 250px)", overflowY: "auto", paddingRight: "8px" }}
                >
                  <div
                    style={{
                      marginBottom: "1rem",
                      display: "flex",
                      alignItems: "flex-end",
                      gap: "1rem",
                    }}
                  >
                    <div style={{ display: "flex", flexDirection: "column", width: "200px" }}>
                      <span style={{ marginBottom: "0.5rem", whiteSpace: "nowrap" }}>ชื่อ-นามสกุล ผู้รับบริการ</span>
                      <Input
                        icon={{ name: "search", circular: false, link: false }}
                        placeholder="ค้นหาด้วยชื่อ หรือ นามสกุล"
                        value={filterName}
                        onChange={(e) => setFilterName(e.target.value)}
                        style={{ width: "100%" }}
                      />
                    </div>

                    <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                      <span>วันที่ขอคืนเงิน</span>
                      <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
                        <FilterDate
                          value={filterRequestRefundStart || ""}
                          onChange={setFilterRequestRefundStart}
                        />
                        <FilterDate
                          value={filterRequestRefundEnd || ""}
                          onChange={setFilterRequestRefundEnd}
                        />
                      </div>
                    </div>

                    <div style={{ display: "flex", gap: "1rem", marginTop: "1.5rem", marginLeft: "auto" }}>
                      <Button
                        style={{ color: "white", backgroundColor: COLOR.primary }}
                        loading={props.buttonLoadCheck?.filterPaymentList === "LOADING"}
                        onClick={handleSearchPaymentClick}
                      >
                        ค้นหา
                      </Button>
                      <Button
                        onClick={() => {
                          setFilterName("");
                          setFilterRequestRefundStart(undefined);
                          setFilterRequestRefundEnd(undefined);
                        }}
                      >
                        ล้างตัวกรอง
                      </Button>
                    </div>
                  </div>
                  <div style={{ fontWeight: "normal" }}>
                    <PaymentSlipTable
                      ref={tableRef}
                      data={requestedRefundData}
                      emptyRows={requestedRefundEmptyRows}
                      status="REQUESTED_REFUND"
                      buttonLoadCheck={props.buttonLoadCheck}
                      onEvent={props.onEvent}
                      appointmentPaymentRelationList={props.appointmentPaymentRelationList}
                      userDetail={props.userDetail}
                      isPaymentRelation={props.isPaymentRelation}
                    />
                  </div>
                  <div style={{ display: "flex", justifyContent: "center", marginTop: "1rem" }}>
                    <Pagination
                      activePage={activePage}
                      totalPages={totalRequestedRefundPages}
                      onPageChange={handlePageChange}
                    />
                  </div>
                </div>
              </Tab.Pane>
            ),
          },
          {
            menuItem: {
              key: "refunded",
              content: (
                <div>
                  <span>คืนเงินสำเร็จ</span>
                  <div className="badge">
                    {masterPaymentList.filter((p: any) => p.status === "REFUNDED").length}
                  </div>{" "}
                </div>
              ),
            },
            render: () => (
              <Tab.Pane>
                <div
                  style={{ height: "calc(100vh - 250px)", overflowY: "auto", paddingRight: "8px" }}
                >
                  <div
                    style={{
                      marginBottom: "1rem",
                      display: "flex",
                      alignItems: "flex-end",
                      gap: "1rem",
                    }}
                  >
                    <div style={{ display: "flex", flexDirection: "column", width: "200px" }}>
                      <span style={{ marginBottom: "0.5rem", whiteSpace: "nowrap" }}>ชื่อ-นามสกุล ผู้รับบริการ</span>
                      <Input
                        icon={{ name: "search", circular: false, link: false }}
                        placeholder="ค้นหาด้วยชื่อ หรือ นามสกุล"
                        value={filterName}
                        onChange={(e) => setFilterName(e.target.value)}
                        style={{ width: "100%" }}
                      />
                    </div>

                    <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                      <span>วันที่ขอคืนเงิน</span>
                      <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
                        <FilterDate
                          value={filterRequestRefundStart || ""}
                          onChange={setFilterRequestRefundStart}
                        />
                        <FilterDate
                          value={filterRequestRefundEnd || ""}
                          onChange={setFilterRequestRefundEnd}
                        />
                      </div>
                    </div>

                    <div style={{ display: "flex", gap: "1rem", marginTop: "1.5rem", marginLeft: "auto" }}>
                      <Button
                        style={{ color: "white", backgroundColor: COLOR.primary }}
                        loading={props.buttonLoadCheck?.filterPaymentList === "LOADING"}
                        onClick={handleSearchPaymentClick}
                      >
                        ค้นหา
                      </Button>
                      <Button
                        onClick={() => {
                          setFilterName("");
                          setFilterRequestRefundStart(undefined);
                          setFilterRequestRefundEnd(undefined);
                        }}
                      >
                        ล้างตัวกรอง
                      </Button>
                    </div>
                  </div>
                  <div style={{ fontWeight: "normal" }}>
                    <PaymentSlipTable
                      ref={tableRef}
                      data={refundedData}
                      emptyRows={refundedEmptyRows}
                      status="REFUNDED"
                      buttonLoadCheck={props.buttonLoadCheck}
                      onEvent={props.onEvent}
                      appointmentPaymentRelationList={props.appointmentPaymentRelationList}
                      userDetail={props.userDetail}
                      isPaymentRelation={props.isPaymentRelation}
                    />
                  </div>
                  <div style={{ display: "flex", justifyContent: "center", marginTop: "1rem" }}>
                    <Pagination
                      activePage={activePage}
                      totalPages={totalRefundedPages}
                      onPageChange={handlePageChange}
                    />
                  </div>
                </div>
              </Tab.Pane>
            ),
          },
        ]}
        onTabChange={handleTabChange}
      />
    </div>
  );
};

export default CardPaymentSlip;
